import { useMemo, useState } from "react";
import { Search } from "lucide-react";

export interface TSearchableCheckboxOption {
    id: string;
    label: string;
    /** Secondary text shown muted after the label; also matched by the search box. */
    sublabel?: string;
}

export interface TSearchableCheckboxListProps {
    options: TSearchableCheckboxOption[];
    selectedIds: string[];
    onChange: (selectedIds: string[]) => void;
    disabled?: boolean;
    loading?: boolean;
    searchPlaceholder?: string;
    /** Shown when there are no options at all (and not loading). */
    emptyMessage?: string;
    /** Shown when there are no options and `loading` is true. */
    loadingMessage?: string;
}

/**
 * A scrollable checkbox list with a search box on top. Lets users filter a long
 * list (e.g. employees) instead of scrolling through all of it. Filtering matches
 * both `label` and `sublabel`, case-insensitively.
 */
export function SearchableCheckboxList({
    options,
    selectedIds,
    onChange,
    disabled = false,
    loading = false,
    searchPlaceholder = "Search...",
    emptyMessage = "No options available",
    loadingMessage = "Loading...",
}: TSearchableCheckboxListProps) {
    const [search, setSearch] = useState("");

    const query = search.trim().toLowerCase();
    const filteredOptions = useMemo(() => {
        if (!query) return options;
        return options.filter(
            (opt) =>
                opt.label.toLowerCase().includes(query) ||
                (opt.sublabel?.toLowerCase().includes(query) ?? false),
        );
    }, [options, query]);

    const toggle = (id: string, checked: boolean) => {
        onChange(checked ? [...selectedIds, id] : selectedIds.filter((v) => v !== id));
    };

    if (options.length === 0) {
        return (
            <div className="border rounded-lg p-3 bg-background">
                <p className="text-sm text-muted-foreground text-center py-2">
                    {loading ? loadingMessage : emptyMessage}
                </p>
            </div>
        );
    }

    return (
        <div className="border rounded-lg bg-background">
            <div className="relative border-b p-2">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    disabled={disabled}
                    className="w-full rounded-md border bg-background py-1 pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2 p-3">
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt) => (
                        <label
                            key={opt.id}
                            className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors"
                        >
                            <input
                                type="checkbox"
                                checked={selectedIds.includes(opt.id)}
                                onChange={(e) => toggle(opt.id, e.target.checked)}
                                disabled={disabled}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <span className="text-sm flex-1">
                                {opt.label}
                                {opt.sublabel && (
                                    <span className="text-muted-foreground ml-1">
                                        ({opt.sublabel})
                                    </span>
                                )}
                            </span>
                        </label>
                    ))
                ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">
                        No matches found
                    </p>
                )}
            </div>
        </div>
    );
}
